/**
 * Shared source-of-truth for which ADF nodes / marks are losslessly
 * representable in our markdown pipeline. Used by:
 *
 *  - `isMarkdownConversionLossy(adf)` — predicate that decides whether the
 *    rich-text → markdown confirmation dialog should be shown.
 *  - `filterToMarkdownSafeAdf(adf)`   — transformer that strips unsupported
 *    nodes / marks before we replace the document on convert.
 *
 * The pipeline opts into these GFM extensions on top of CommonMark:
 *   - TablesExtension          (GFM §4.10)
 *   - TaskListItemsExtension   (GFM §5.3)
 *   - StrikethroughExtension   (GFM §6.5)
 *   - AutolinkExtension        (GFM §6.9)
 */

export const SUPPORTED_NODE_TYPES: ReadonlySet<string> = new Set([
	// Document root
	'doc',
	// Block
	'paragraph',
	'heading',
	'blockquote',
	'codeBlock',
	'bulletList',
	'orderedList',
	'listItem',
	'rule',
	// GFM table (basic — see merged-cell check at the call site)
	'table',
	'tableRow',
	'tableHeader',
	'tableCell',
	// GFM task list items (rendered as `- [ ]` / `- [x]`)
	'taskList',
	'taskItem',
	// Inline
	'text',
	'hardBreak',
]);

export const SUPPORTED_MARK_TYPES: ReadonlySet<string> = new Set([
	'em',
	'strong',
	'strike',
	'code',
	'link',
]);

/** Returns true if a tableCell / tableHeader has a colspan or rowspan > 1. */
export const hasMergedCellSpan = (node: {
	attrs?: { colspan?: unknown; rowspan?: unknown };
}): boolean => {
	const colspan = node.attrs?.colspan;
	const rowspan = node.attrs?.rowspan;
	return (
		(typeof colspan === 'number' && colspan > 1) || (typeof rowspan === 'number' && rowspan > 1)
	);
};
